// Control UI module implements activity model behavior.
import { asNullableObjectRecord as readRecord } from "@openclaw/normalization-core/record-coerce";
import { normalizeNullableString as toTrimmedString } from "@openclaw/normalization-core/string-coerce";
import { redactToolPayloadText } from "../../lib/browser-redact.ts";
import { formatUnknownText, truncateText } from "../../lib/format.ts";

const ACTIVITY_ENTRY_LIMIT = 100;
const ACTIVITY_OUTPUT_PREVIEW_LIMIT = 2_000;

export type ActivityStatus = "running" | "done" | "error";

export type ActivityEntry = {
  id: string;
  toolCallId: string;
  runId: string;
  sessionKey?: string;
  toolName: string;
  entryKind: "tool" | "answer_candidate";
  itemId?: string;
  candidateStatus?: "candidate" | "superseded" | "selected";
  status: ActivityStatus;
  startedAt: number;
  updatedAt: number;
  durationMs: number;
  outputPreview?: string;
  outputTruncated: boolean;
  summary: string;
  hiddenArgumentCount: number;
};

const ACTIVITY_STATUS_SUMMARY_LABELS: Record<ActivityStatus, string> = {
  running: "running",
  done: "completed",
  error: "failed",
};

type ActivityEvent = {
  stream: "tool" | "item";
  runId: string;
  ts: number;
  receivedAt: number;
  sessionKey?: string;
  agentId?: string;
  data: Record<string, unknown>;
};

export function parseActivityEvent(
  payload: unknown,
  receivedAt = Date.now(),
): ActivityEvent | null {
  const record = readRecord(payload);
  const runId = toTrimmedString(record?.runId);
  const data = readRecord(record?.data);
  const isTool = record?.stream === "tool";
  const isAnswerCandidate =
    record?.stream === "item" && toTrimmedString(data?.kind) === "answer_candidate";
  if (!record || (!isTool && !isAnswerCandidate) || !runId || !data) {
    return null;
  }
  const sessionKey = toTrimmedString(record.sessionKey);
  const agentId = toTrimmedString(record.agentId);
  return {
    stream: isTool ? "tool" : "item",
    runId,
    ts: typeof record.ts === "number" ? record.ts : receivedAt,
    receivedAt,
    ...(sessionKey ? { sessionKey } : {}),
    ...(agentId ? { agentId } : {}),
    data,
  };
}

function extractText(value: unknown): string | null {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  const record = readRecord(value);
  if (!record) {
    return null;
  }
  if (typeof record.text === "string") {
    return record.text;
  }
  const content = record.content;
  if (!Array.isArray(content)) {
    return null;
  }
  const parts = content
    .map((item) => {
      const entry = readRecord(item);
      return entry?.type === "text" && typeof entry.text === "string" ? entry.text : null;
    })
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join("\n") : null;
}

function stringifyOutput(value: unknown): string | null {
  const text = extractText(value);
  if (text !== null) {
    return text;
  }
  if (value === null || value === undefined) {
    return null;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return formatUnknownText(value);
  }
}

function buildOutputPreview(value: unknown): { text?: string; truncated: boolean } {
  const raw = stringifyOutput(value);
  if (!raw) {
    return { truncated: false };
  }
  const redacted = redactToolPayloadText(raw);
  const truncated = truncateText(redacted, ACTIVITY_OUTPUT_PREVIEW_LIMIT);
  // A sliced preview can retain the whole output after the raw event is evicted.
  // Copy its characters here so the bounded Activity entry owns only its preview.
  return { text: Array.from(truncated.text).join(""), truncated: truncated.truncated };
}

function countArgumentFields(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (Array.isArray(value)) {
    return value.length;
  }
  const record = readRecord(value);
  if (record) {
    return Object.keys(record).length;
  }
  return 1;
}

function hasExplicitErrorFlag(value: Record<string, unknown> | null): boolean {
  return value?.isError === true || value?.is_error === true;
}

function resolveStatus(data: Record<string, unknown>): ActivityStatus {
  const phase = toTrimmedString(data.phase);
  if (phase !== "result") {
    return "running";
  }
  const result = readRecord(data.result);
  if (hasExplicitErrorFlag(data) || hasExplicitErrorFlag(result)) {
    return "error";
  }
  const status = toTrimmedString(data.status) ?? toTrimmedString(result?.status);
  if (status && /error|fail|failed|failure/i.test(status)) {
    return "error";
  }
  const exitCode = Number(result?.exitCode ?? data.exitCode);
  if (Number.isFinite(exitCode) && exitCode !== 0) {
    return "error";
  }
  return "done";
}

function statusLabel(status: ActivityStatus): string {
  return ACTIVITY_STATUS_SUMMARY_LABELS[status];
}

function buildSummary(toolName: string, status: ActivityStatus, hiddenArgCount: number): string {
  const argText = `${hiddenArgCount} argument${hiddenArgCount === 1 ? "" : "s"} hidden`;
  return `${toolName} ${statusLabel(status)}; ${argText}`;
}

export function updateToolActivity(
  entries: ActivityEntry[],
  payload: ActivityEvent,
): ActivityEntry[] {
  const data = payload.data ?? {};
  if (payload.stream === "item") {
    return updateAnswerCandidateActivity(entries, payload);
  }
  const toolCallId = toTrimmedString(data.toolCallId);
  if (!toolCallId) {
    return entries;
  }
  const toolName = toTrimmedString(data.name) ?? "tool";
  const id = `${payload.runId}:${toolCallId}`;
  const now = payload.receivedAt;
  const startedAt = typeof payload.ts === "number" ? payload.ts : now;
  const status = resolveStatus(data);
  const outputValue =
    data.phase === "update" ? data.partialResult : data.phase === "result" ? data.result : null;
  const preview = buildOutputPreview(outputValue);
  const existing = entries.find((entry) => entry.id === id);
  const hiddenArgCount =
    data.args !== undefined ? countArgumentFields(data.args) : (existing?.hiddenArgumentCount ?? 0);
  const outputPreview = preview.text ?? existing?.outputPreview;
  const nextEntry: ActivityEntry = {
    id,
    toolCallId,
    runId: payload.runId,
    ...(payload.sessionKey ? { sessionKey: payload.sessionKey } : {}),
    toolName,
    entryKind: "tool",
    status,
    startedAt: existing?.startedAt ?? startedAt,
    updatedAt: now,
    durationMs: Math.max(0, now - (existing?.startedAt ?? startedAt)),
    outputTruncated: preview.truncated || existing?.outputTruncated === true,
    summary: buildSummary(toolName, status, hiddenArgCount),
    hiddenArgumentCount: hiddenArgCount,
    ...(outputPreview ? { outputPreview } : {}),
  };
  const next = existing
    ? entries.map((entry) => (entry.id === id ? nextEntry : entry))
    : [...entries, nextEntry];
  return next.slice(-ACTIVITY_ENTRY_LIMIT);
}

function readAnswerCandidateStatus(value: unknown): "candidate" | "superseded" | "selected" | null {
  return value === "candidate" || value === "superseded" || value === "selected" ? value : null;
}

function updateAnswerCandidateActivity(
  entries: ActivityEntry[],
  payload: ActivityEvent,
): ActivityEntry[] {
  const itemId = toTrimmedString(payload.data.itemId);
  const candidateStatus = readAnswerCandidateStatus(payload.data.status);
  if (!itemId || !candidateStatus) {
    return entries;
  }
  const id = `${payload.runId}:answer_candidate:${itemId}`;
  const existing = entries.find((entry) => entry.id === id);
  const now = payload.receivedAt;
  const startedAt = existing?.startedAt ?? payload.ts;
  const preview = buildOutputPreview(payload.data.progressText);
  const nextEntry: ActivityEntry = {
    id,
    toolCallId: itemId,
    itemId,
    runId: payload.runId,
    ...(payload.sessionKey ? { sessionKey: payload.sessionKey } : {}),
    toolName: "answer_candidate",
    entryKind: "answer_candidate",
    candidateStatus,
    status: candidateStatus === "candidate" ? "running" : "done",
    startedAt,
    updatedAt: now,
    durationMs: Math.max(0, now - startedAt),
    outputTruncated: preview.truncated || existing?.outputTruncated === true,
    summary: `answer_candidate.${candidateStatus}`,
    hiddenArgumentCount: 0,
    ...(preview.text ? { outputPreview: preview.text } : {}),
  };
  const next = existing
    ? entries.map((entry) => (entry.id === id ? nextEntry : entry))
    : [...entries, nextEntry];
  return next.slice(-ACTIVITY_ENTRY_LIMIT);
}
